import { Link } from 'react-router';
import Footer from '../components/Footer'
import Nav from '../components/Nav';
import { useState, useEffect, type ReactNode } from 'react';
import { useThemeColors } from '../hooks/useThemeColors';

export default function Privacy() {
  const c = useThemeColors();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const H2 = ({ children }: { children: ReactNode }) => (
    <h2
      style={{
        fontFamily: 'Inter, sans-serif',
        fontWeight: 700,
        fontSize: 22,
        letterSpacing: '-0.02em',
        color: c.textPrimary,
        margin: '40px 0 12px',
        lineHeight: 1.3,
      }}
    >
      {children}
    </h2>
  );

  const H3 = ({ children }: { children: ReactNode }) => (
    <h3
      style={{
        fontFamily: 'Inter, sans-serif',
        fontWeight: 600,
        fontSize: 16,
        color: c.textPrimary,
        margin: '24px 0 8px',
        lineHeight: 1.4,
      }}
    >
      {children}
    </h3>
  );

  const P = ({ children }: { children: ReactNode }) => (
    <p style={{ color: c.textMuted, fontSize: 15, lineHeight: '1.7', margin: '0 0 12px' }}>
      {children}
    </p>
  );

  const UL = ({ children }: { children: ReactNode }) => (
    <ul style={{ color: c.textMuted, fontSize: 15, lineHeight: '1.7', margin: '0 0 12px', paddingLeft: 22 }}>
      {children}
    </ul>
  );

  return (
    <div style={{ background: c.bgPrimary, minHeight: '100vh' }}>
      <Nav scrolled={scrolled} />
      <div
        style={{
          maxWidth: 700,
          margin: '0 auto',
          padding: '96px 24px',
        }}
      >
        {/* Label */}
        <p
          style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 11,
            color: c.signal,
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            margin: '0 0 16px',
          }}
        >
          LEGAL
        </p>

        {/* H1 */}
        <h1
          style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 800,
            fontSize: 48,
            letterSpacing: '-0.03em',
            color: c.textPrimary,
            margin: '0 0 12px',
            lineHeight: 1.1,
          }}
        >
          Privacy Policy
        </h1>
        <p
          style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 12,
            color: c.textMuted,
            margin: '0 0 32px',
          }}
        >
          Effective date: August 1, 2026
        </p>

        <P>
          Primate AI, Inc. (doing business as “Primate Intelligence”, “we”, “us”, or “our”) provides
          visual-intelligence products and services, including Primate Vision, at
          primateintelligence.ai (the “Service”). This Privacy Policy explains what information we
          collect, how we use it, who we share it with, how we protect it, and the choices you have.
        </P>

        <H2>1. Information We Collect</H2>
        <H3>Account information</H3>
        <P>
          When you create an account, we collect your name, email address, and profile picture. You
          can sign up with an email address and password, or through a third-party sign-in provider
          (Google or GitHub). Authentication is handled by our identity provider, Clerk.
        </P>
        <H3>Content you provide</H3>
        <P>
          Videos, images, prompts, and other content you upload or submit for analysis, along with
          the results the Service generates from them.
        </P>
        <H3>Streaming sessions and prompts</H3>
        <P>
          When you use our streaming features, we record and retain your full session — every
          prompt you submit and the video processed during the session, including frames from your
          camera or from video you upload. This applies whether or not you use an in-product record
          button, and whether you are signed in or using the Service anonymously. We use this
          content to provide and operate the Service and to analyze quality and safety.
          Anonymised analysis results (not your original video) may be used to train and improve
          our models — we never use raw video for training without explicit separate consent. By
          using the streaming features, you consent to this recording,
          retention, and use. See Section 6 for retention and deletion.
        </P>
        <H3>Payment information</H3>
        <P>
          If you purchase a paid plan, payments are processed by Stripe. We receive limited billing
          details (such as plan, status, and last four card digits) but never store full card
          numbers.
        </P>
        <H3>Usage data</H3>
        <P>
          We collect information about how you use the Service — pages visited, features used,
          device and browser type, IP address, and cookies or similar technologies — through
          analytics tools including PostHog and Google Analytics.
        </P>

        <H2>2. Google User Data</H2>
        <P>
          This section applies when you choose to sign in to the Service with your Google account,
          and describes our handling of Google user data in accordance with the Google API Services
          User Data Policy.
        </P>
        <H3>Data accessed</H3>
        <P>
          If you sign in with Google, we request only the basic sign-in scopes (<code>openid</code>,{' '}
          <code>email</code>, <code>profile</code>). Through these we access: your name, your email
          address, your profile picture, and your Google account identifier. We do not access,
          request, or store any other Google user data — no Gmail, Google Drive, Calendar, Contacts,
          Photos, or any other Google service content.
        </P>
        <H3>Data usage</H3>
        <P>
          We use this information solely to create and authenticate your account, identify you
          within the Service (for example, displaying your name and photo), and send you
          service-related communications. We do not use Google user data for advertising, and we do
          not use Google user data to train or improve machine-learning or AI models.
        </P>
        <P>
          Primate Intelligence’s use of information received from Google APIs will adhere to the{' '}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: c.signal, textDecoration: 'none' }}
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </P>
        <H3>Data sharing</H3>
        <P>
          We do not sell Google user data, and we do not share it with third parties for
          advertising. Google user data is shared only with the service providers that are necessary
          to operate the Service — Clerk (authentication) and Supabase (database hosting) — under
          agreements that restrict their use of the data to providing services to us. We may also
          disclose it where required by law, or as part of a merger or acquisition (in which case
          this policy will continue to apply to it). Our employees do not access Google user data
          except as needed for security, support you request, or legal compliance.
        </P>
        <H3>Data storage and protection</H3>
        <P>
          Google user data is stored on access-controlled infrastructure operated by our processors
          (Clerk and Supabase, on major cloud providers). It is encrypted in transit using TLS and
          encrypted at rest. Access is limited to authorized personnel and systems using
          role-based access controls and audited credentials.
        </P>
        <H3>Data retention and deletion</H3>
        <P>
          We retain Google user data for as long as your account is active. You can delete your
          account at any time from your account settings, or request deletion by emailing{' '}
          <a href="mailto:privacy@primateintelligence.ai" style={{ color: c.signal, textDecoration: 'none' }}>
            privacy@primateintelligence.ai
          </a>
          . When your account is deleted, we delete your Google user data from our active systems
          within 30 days; residual copies in encrypted backups are purged within 90 days. You can
          also revoke our access to your Google account at any time via your{' '}
          <a
            href="https://myaccount.google.com/permissions"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: c.signal, textDecoration: 'none' }}
          >
            Google account permissions
          </a>
          .
        </P>

        <H2>3. How We Use Information</H2>
        <UL>
          <li>Provide, operate, and improve the Service</li>
          <li>Authenticate you and secure your account</li>
          <li>Process your uploads and deliver analysis results</li>
          <li>Process payments and manage subscriptions</li>
          <li>Respond to support requests and send service communications</li>
          <li>Understand product usage to improve features (analytics)</li>
          <li>
            Train and improve our models, using content you submit — including recorded streaming
            sessions and prompts (see “Streaming sessions and prompts” above)
          </li>
          <li>Detect, prevent, and investigate abuse, fraud, or security incidents</li>
        </UL>

        <H2>4. How We Share Information</H2>
        <P>
          We do not sell your personal information. We share it only with service providers that
          help us run the Service, each limited to what they need:
        </P>
        <UL>
          <li>Authentication — Clerk</li>
          <li>Hosting and infrastructure — Vercel, Railway, Amazon Web Services, Google Cloud, Supabase</li>
          <li>Payments — Stripe</li>
          <li>Analytics — PostHog, Google Analytics</li>
          <li>Email and support — Resend, Intercom</li>
        </UL>
        <P>
          We may also disclose information if required by law, to protect our rights or users’
          safety, or in connection with a merger, acquisition, or sale of assets.
        </P>

        <H2>5. Security</H2>
        <P>
          We protect your data with encryption in transit (TLS) and at rest, role-based access
          controls, credential management, and monitoring. No method of transmission or storage is
          100% secure, but we work to protect your information against unauthorized access,
          alteration, disclosure, or destruction.
        </P>

        <H2>6. Data Retention and Deletion</H2>
        <P>
          We keep your personal information while your account is active or as needed to provide the
          Service. Videos you save to your library, session recordings, and analysis results are
          retained until you delete them from your dashboard. Recorded streaming sessions and
          prompts are retained for the purposes described in Section 1 until you delete them or
          request their deletion; to request deletion of your account and all data — including
          sessions used while signed out — email{' '}
          <a href="mailto:privacy@primateintelligence.ai" style={{ color: c.signal, textDecoration: 'none' }}>
            privacy@primateintelligence.ai
          </a>{' '}
          and we will respond within 30 days. When you delete your account — from account settings or by emailing{' '}
          <a href="mailto:privacy@primateintelligence.ai" style={{ color: c.signal, textDecoration: 'none' }}>
            privacy@primateintelligence.ai
          </a>{' '}
          — we delete your personal information from active systems within 30 days, and from
          encrypted backups within 90 days, except where retention is required by law (for example,
          billing records).
        </P>

        <H2>7. Your Rights</H2>
        <P>
          Depending on where you live, you may have rights to access, correct, export, or delete
          your personal information, or to object to or restrict certain processing. To exercise any
          of these rights, email{' '}
          <a href="mailto:privacy@primateintelligence.ai" style={{ color: c.signal, textDecoration: 'none' }}>
            privacy@primateintelligence.ai
          </a>
          . We will respond within 30 days.
        </P>

        <H2>8. Children</H2>
        <P>
          The Service is not directed to children under 13, and we do not knowingly collect personal
          information from them. If you believe a child has provided us personal information,
          contact us and we will delete it.
        </P>

        <H2>9. Changes to This Policy</H2>
        <P>
          We may update this policy from time to time. We will post the updated version on this page
          and update the effective date above. For material changes, we will provide additional
          notice (such as email or an in-product notice).
        </P>

        <H2>10. Contact Us</H2>
        <P>
          Primate AI, Inc. (dba Primate Intelligence)
          <br />
          1210 Redmond Ave, San Jose, CA 95120, USA
          <br />
          <a href="mailto:privacy@primateintelligence.ai" style={{ color: c.signal, textDecoration: 'none' }}>
            privacy@primateintelligence.ai
          </a>
        </P>

        {/* Back link */}
        <Link
          to="/primate-vision"
          style={{ color: c.signal, fontSize: 14, textDecoration: 'none', fontWeight: 500, display: 'inline-block', marginTop: 32 }}
        >
          ← Back to Primate Vision
        </Link>
      </div>
      <Footer />
    </div>
  );
}
